import React from "react";
import { MapPin, Loader2, CheckCircle, Plus, Home, Building, Check } from "lucide-react";
import { BookingLocationPicker } from "./BookingLocationPicker";

/**
 * BookingAddressSelector — address picker used inside OnlineBookingSection.
 *
 * Amazon-style saved-addresses grid for logged-in customers + inline
 * "new address" form with live pincode serviceability check.
 *
 * Extracted from OnlineBookingSection.js (iter150 refactor) — behaviour
 * preserved exactly. All data-testids preserved for E2E parity.
 */
export const BookingAddressSelector = ({
  formData,
  setFormData,
  isCustomerLoggedIn,
  savedAddresses,
  selectedAddressId,
  setSelectedAddressId,
  showNewAddressForm,
  setShowNewAddressForm,
  pincodeStatus,
  setPincodeStatus,
  pincodeLoading,
}) => {
  const showInlineForm = !isCustomerLoggedIn || showNewAddressForm || savedAddresses.length === 0;
  return (
    <div>
      <label className="block text-sm font-medium text-[#1E1B4B] mb-2 flex items-center gap-2">
        <MapPin size={16} /> Delivery Address *
      </label>

      {/* Saved Address Cards (Amazon-style) — shown when logged in & has addresses */}
      {isCustomerLoggedIn && savedAddresses.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 mb-3" data-testid="saved-addresses">
          {savedAddresses.map((addr) => {
            const isSelected = selectedAddressId === addr.address_id;
            const isOfficeLabel =
              addr.label?.toLowerCase().includes("office") ||
              addr.label?.toLowerCase().includes("work");
            return (
              <button
                key={addr.address_id}
                type="button"
                onClick={() => {
                  setSelectedAddressId(addr.address_id);
                  setShowNewAddressForm(false);
                  setFormData((prev) => ({
                    ...prev,
                    address: addr.address,
                    pincode: addr.pincode || prev.pincode,
                  }));
                }}
                className={`relative text-left p-3 rounded-xl border-2 transition-all ${
                  isSelected
                    ? "border-[#E83E8C] bg-[#E83E8C]/5 shadow-sm"
                    : "border-gray-200 hover:border-[#E83E8C]/40 bg-white"
                }`}
                data-testid={`address-card-${addr.address_id}`}
              >
                {isSelected && (
                  <div className="absolute top-2 right-2 w-5 h-5 rounded-full bg-[#E83E8C] flex items-center justify-center">
                    <Check size={12} className="text-white" />
                  </div>
                )}
                <div className="flex items-center gap-1.5 mb-1">
                  {isOfficeLabel ? (
                    <Building size={13} className="text-[#475569]" />
                  ) : (
                    <Home size={13} className="text-[#475569]" />
                  )}
                  <span className="text-xs font-semibold text-[#1E1B4B] uppercase tracking-wide">
                    {addr.label}
                  </span>
                  {addr.is_default && (
                    <span className="text-[9px] px-1.5 py-0.5 bg-[#E83E8C]/10 text-[#E83E8C] rounded-full font-medium">
                      Default
                    </span>
                  )}
                </div>
                <p className="text-xs text-[#475569] line-clamp-2 leading-relaxed">{addr.address}</p>
                {addr.pincode && (
                  <p className="text-[10px] text-[#94a3b8] mt-0.5">PIN: {addr.pincode}</p>
                )}
              </button>
            );
          })}

          {/* Add New Address Card */}
          <button
            type="button"
            onClick={() => {
              setSelectedAddressId(null);
              setShowNewAddressForm(true);
              setFormData((prev) => ({ ...prev, address: "", pincode: "" }));
              setPincodeStatus(null);
            }}
            className={`flex flex-col items-center justify-center p-3 rounded-xl border-2 border-dashed transition-all ${
              showNewAddressForm && !selectedAddressId
                ? "border-[#E83E8C] bg-[#E83E8C]/5"
                : "border-gray-300 hover:border-[#E83E8C]/40 bg-gray-50/50"
            }`}
            data-testid="add-new-address-btn"
          >
            <Plus size={18} className="text-[#E83E8C] mb-1" />
            <span className="text-xs font-medium text-[#475569]">New Address</span>
          </button>
        </div>
      )}

      {/* Pincode + Address Fields — show when: not logged in, OR logged in but adding new, OR no saved addresses */}
      {showInlineForm && (
        <div className="space-y-3">
          {/* Google Maps picker — fills pincode + address + precise lat/lng
              instantly. Customers can still refine the textarea with
              flat/floor/landmarks. */}
          <BookingLocationPicker
            onLocationPicked={({ address, pincode, lat, lng }) => {
              setFormData((prev) => ({
                ...prev,
                // Only overwrite address/pincode if the picker actually
                // resolved them — otherwise keep whatever the customer typed.
                address: address || prev.address,
                pincode: pincode || prev.pincode,
                // Lat/lng always carry forward so the booking payload has
                // the exact pin even if geocoding failed.
                location_lat: lat ?? prev.location_lat,
                location_lng: lng ?? prev.location_lng,
              }));
              if (!pincode) setPincodeStatus(null);
            }}
          />
          <div>
            <label className="block text-xs font-medium text-[#475569] mb-1">Pincode *</label>
            <input
              type="text"
              required
              maxLength={6}
              inputMode="numeric"
              value={formData.pincode}
              onChange={(e) => {
                const val = e.target.value.replace(/\D/g, "").slice(0, 6);
                setFormData({ ...formData, pincode: val });
              }}
              className={`w-full px-4 py-3 rounded-xl border-2 outline-none transition-all ${
                pincodeStatus?.serviceable === true
                  ? "border-[#25D366] bg-[#25D366]/5"
                  : pincodeStatus?.serviceable === false
                  ? "border-amber-400 bg-amber-50"
                  : "border-gray-200 focus:border-[#E83E8C] focus:ring-2 focus:ring-[#E83E8C]/20"
              }`}
              placeholder="e.g. 400001"
              data-testid="pincode-input"
            />
            {pincodeLoading && (
              <p className="text-xs text-[#94a3b8] mt-1 flex items-center gap-1">
                <Loader2 size={12} className="animate-spin" /> Checking pincode...
              </p>
            )}
            {pincodeStatus && !pincodeLoading && (
              <div
                className={`mt-1.5 px-3 py-2 rounded-lg text-xs font-medium ${
                  pincodeStatus.serviceable
                    ? "bg-[#25D366]/10 text-[#25D366]"
                    : "bg-amber-50 text-amber-700 border border-amber-200"
                }`}
                data-testid="pincode-status"
              >
                {pincodeStatus.serviceable ? (
                  <span className="flex items-center gap-1">
                    <CheckCircle size={13} /> {pincodeStatus.message}
                  </span>
                ) : (
                  <span>{pincodeStatus.message}</span>
                )}
              </div>
            )}
          </div>
          <div>
            <label className="block text-xs font-medium text-[#475569] mb-1">Full Address *</label>
            <textarea
              required
              value={formData.address}
              onChange={(e) => setFormData({ ...formData, address: e.target.value })}
              rows={2}
              className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-[#E83E8C] outline-none resize-none"
              placeholder="Flat/House no., floor, apartment name, landmark..."
              data-testid="address-input"
            />
            <p className="text-[10px] text-[#94a3b8] mt-1">
              Auto-filled from the pin above. Edit to add flat/floor and landmarks for a precise setup location.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};
